import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  LayoutDashboard, Users, Megaphone, FileText, Landmark, Calendar, CheckSquare, Inbox as InboxIcon,
  Mail, Bell, LogOut, Search, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { label: "Dashboard",    to: "/admin",              icon: LayoutDashboard, keywords: "home overview stats" },
  { label: "Inbox",        to: "/admin/inbox",        icon: InboxIcon,       keywords: "notifications messages signals incoming" },
  { label: "Tasks",        to: "/admin/tasks",        icon: CheckSquare,     keywords: "todo assignments due dates" },
  { label: "Contacts",     to: "/admin/contacts",     icon: Users,           keywords: "people network advocates crm" },
  { label: "Campaigns",    to: "/admin/campaigns",    icon: Megaphone,       keywords: "action alerts" },
  { label: "Bills",        to: "/admin/bills",        icon: Landmark,        keywords: "legislative tracker legislation" },
  { label: "Statements",   to: "/admin/statements",   icon: FileText,        keywords: "letters testimony content" },
  { label: "Events",       to: "/admin/events",       icon: Calendar,        keywords: "rsvp calendar" },
  { label: "Emails",       to: "/admin/emails",       icon: Mail,            keywords: "broadcast newsletter automations" },
  { label: "Announcement", to: "/admin/announcement", icon: Bell,            keywords: "banner top bar" },
];

/**
 * Global admin command palette. Open with ⌘K / Ctrl+K.
 * Navigates to admin sections + searches contacts, bills, alerts, statements.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ contacts: [], bills: [], alerts: [], statements: [] });
  const navigate = useNavigate();
  const { logout } = useAuth();
  const debounceRef = useRef(null);

  // Toggle with ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Reset query on close
  useEffect(() => { if (!open) { setQ(""); setResults({ contacts: [], bills: [], alerts: [], statements: [] }); } }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) return;
    debounceRef.current = setTimeout(async () => {
      try {
        const [pub, contacts] = await Promise.all([
          api.get("/public/search", { params: { q } }),
          api.get("/admin/contacts", { params: { q, limit: 6 } }),
        ]);
        const publicResults = pub.data.results || [];
        setResults({
          contacts: contacts.data || [],
          bills: publicResults.filter(r => r.type === "bill"),
          alerts: publicResults.filter(r => r.type === "action_alert"),
          statements: publicResults.filter(r => r.type === "statement"),
        });
      } catch {
        setResults({ contacts: [], bills: [], alerts: [], statements: [] });
      }
    }, 180);
  }, [q]);

  const go = (path) => { setOpen(false); navigate(path); };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-black/50"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl bg-white border border-stone-300 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <Command shouldFilter={false} label="Global command palette">
          <div className="flex items-center gap-2 px-4 border-b border-stone-200">
            <Search size={16} className="text-stone-400"/>
            <Command.Input
              value={q}
              onValueChange={setQ}
              placeholder="Type a command or search…"
              className="flex-1 py-3 text-sm bg-transparent focus:outline-none"
              data-testid="command-palette-input"
              autoFocus
            />
            <kbd className="text-[10px] uppercase tracking-wider text-stone-400 border border-stone-300 px-1.5 py-0.5">esc</kbd>
          </div>
          <Command.List className="max-h-[420px] overflow-y-auto">
            <Command.Empty className="px-4 py-6 text-sm text-stone-500">No results.</Command.Empty>

            <Command.Group heading="Navigation" className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-stone-500">
              {NAV_ITEMS.map(item => (
                <Command.Item
                  key={item.to}
                  value={`${item.label} ${item.keywords}`}
                  onSelect={() => go(item.to)}
                  className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white flex items-center gap-3"
                >
                  <item.icon size={14}/> {item.label}
                </Command.Item>
              ))}
              <Command.Item value="sign out logout" onSelect={() => { setOpen(false); logout(); }}
                className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white flex items-center gap-3">
                <LogOut size={14}/> Sign out
              </Command.Item>
            </Command.Group>

            {results.contacts.length > 0 && (
              <Command.Group heading="Contacts" className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ccc-gold-dark">
                {results.contacts.map(c => (
                  <Command.Item key={c.id} value={`contact ${c.name} ${c.email}`}
                    onSelect={() => go(`/admin/contacts?open=${c.id}`)}
                    className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white flex items-center justify-between gap-3">
                    <span><span className="font-medium">{c.name}</span> <span className="text-stone-500">{c.email}</span></span>
                    <ExternalLink size={12} className="text-stone-400"/>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {results.bills.length > 0 && (
              <Command.Group heading="Bills" className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ccc-gold-dark">
                {results.bills.map(b => (
                  <Command.Item key={b.id} value={`bill ${b.title}`}
                    onSelect={() => go(b.url)}
                    className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white">
                    {b.title} <span className="text-stone-500">— {b.subtitle}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {results.alerts.length > 0 && (
              <Command.Group heading="Action alerts" className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ccc-gold-dark">
                {results.alerts.map(a => (
                  <Command.Item key={a.id} value={`alert ${a.title}`}
                    onSelect={() => go(a.url)}
                    className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white">
                    {a.title}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {results.statements.length > 0 && (
              <Command.Group heading="Statements" className="[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-ccc-gold-dark">
                {results.statements.map(s => (
                  <Command.Item key={s.id} value={`statement ${s.title}`}
                    onSelect={() => go(s.url)}
                    className="px-4 py-2 text-sm cursor-pointer aria-selected:bg-ccc-plum aria-selected:text-white">
                    {s.title}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
          <div className="border-t border-stone-200 px-4 py-2 text-[10px] uppercase tracking-wider text-stone-500 flex items-center justify-between">
            <span>↑↓ navigate · ↵ open · esc close</span>
            <kbd className="border border-stone-300 px-1.5 py-0.5">⌘K</kbd>
          </div>
        </Command>
      </div>
    </div>
  );
}
